// Kohteiden ja reittien järjestys sijainnin mukaan: lähin alue ensin.
//
// Valmiilla kohteilla ei ole vielä tarkkoja koordinaatteja (ne asetetaan kartalla, suunnitelman kohta 15),
// joten järjestys tehdään kahdella tasolla:
//   1. Alueella (esim. Joutseno, Imatra) on KARKEA keskipiste, jota käytetään vain järjestykseen.
//      Se ei ole kohteen paikka eikä se näy missään karttana; tarkkuus on kilometrin luokkaa.
//      Näin Lappeenrannan kohteet tulevat ennen Imatran kohteita, kun olet Lappeenrannassa.
//   2. Kohteen tai reitin oma tarkka sijainti, jos se on asetettu, ohittaa alueen keskipisteen
//      ja järjestää kohteet alueen sisällä lähimmästä alkaen.
// Puhdasta koodia, jotta säännöt voi testata ilman näkymää (käyttö: ContentView.jsx).

import { distanceM } from "./geo.js";
import { routeVertices } from "./routeGeometry.js";

/** Kuntien karkeat keskipisteet [lat, lon]. Vain järjestykseen. */
export const MUNICIPALITY_CENTRES = {
  Lappeenranta: [61.0587, 28.1887],
  Imatra: [61.1719, 28.7523],
  Taipalsaari: [61.1578, 28.046],
  Savitaipale: [61.202, 27.683],
  Ruokolahti: [61.29, 28.824],
  Parikkala: [61.55, 29.5],
  Rautjärvi: [61.43, 29.33],
  Lemi: [61.067, 27.8],
  Luumäki: [60.917, 27.567],
};

/** Lappeenrannan kaupunginosat ja kylät, jotka ovat selvästi keskustan ulkopuolella (avain: "kunta|alue"). */
export const AREA_CENTRES = {
  "Lappeenranta|Joutseno": [61.1167, 28.5],
  "Lappeenranta|Nuijamaa": [60.99, 28.39],
  "Lappeenranta|Lauritsala": [61.047, 28.228],
};

/** Alueen tai kunnan karkea keskipiste, tai null jos kuntaa ei tunneta. */
export function areaCentre(item) {
  return AREA_CENTRES[`${item.municipality}|${item.area}`] ?? MUNICIPALITY_CENTRES[item.municipality] ?? null;
}

function nearestVertexM(geometry, pos) {
  let best = Infinity;
  for (const p of routeVertices(geometry)) {
    if (!Array.isArray(p) || !Number.isFinite(p[0]) || !Number.isFinite(p[1])) continue;
    const d = distanceM(pos.latitude, pos.longitude, p[0], p[1]);
    if (d < best) best = d;
  }
  return Number.isFinite(best) ? best : null;
}

function validPos(pos) {
  return Boolean(pos) && Number.isFinite(pos.latitude) && Number.isFinite(pos.longitude);
}

/**
 * Tarkka etäisyys metreinä kohteen (kind "poi") asetettuun pisteeseen tai reitin (kind "route")
 * lähimpään pisteeseen. null, jos sijaintia tai kohteen paikkaa ei ole.
 */
export function exactDistanceM(kind, item, pos) {
  if (!validPos(pos)) return null;
  if (kind === "poi") {
    return Number.isFinite(item.lat) && Number.isFinite(item.lon) ? distanceM(pos.latitude, pos.longitude, item.lat, item.lon) : null;
  }
  return Array.isArray(item.geometry) && item.geometry.length ? nearestVertexM(item.geometry, pos) : null;
}

function groupLabel(item) {
  return item.area === item.municipality ? item.municipality : `${item.area}, ${item.municipality}`;
}

/**
 * Alueryhmät lähimmästä kaukaisimpaan. Palauttaa
 *   [{ label, items: [{ item, meters }], meters, approx }]
 * `meters` on ryhmän etäisyys (tarkka, jos jollakin ryhmän kohteella on oma sijainti, muuten alueen
 * keskipisteestä, jolloin `approx` on true) tai null, jos sitä ei voi laskea; sellaiset ryhmät ovat lopussa.
 * Ryhmän sisällä kohteet joilla on oma sijainti ovat ensin lähimmästä alkaen, muut perässä nimijärjestyksessä.
 * Ilman sijaintia järjestys on sama kuin alkuperäisessä listassa. Tasatilanteessa ryhmät nimen mukaan.
 */
export function groupsByDistance(kind, items, pos) {
  const groups = new Map();
  items.forEach((item, index) => {
    const label = groupLabel(item);
    if (!groups.has(label)) groups.set(label, { label, items: [], first: index, centre: areaCentre(item) });
    groups.get(label).items.push({ item, meters: exactDistanceM(kind, item, pos) });
  });
  const result = [...groups.values()].map((g) => {
    g.items.sort((a, b) => {
      if (a.meters !== null && b.meters !== null) return a.meters - b.meters || a.item.name.localeCompare(b.item.name, "fi");
      if (a.meters !== null) return -1;
      if (b.meters !== null) return 1;
      return a.item.name.localeCompare(b.item.name, "fi");
    });
    const exact = g.items.map((x) => x.meters).filter((m) => m !== null);
    let meters = null;
    let approx = false;
    if (validPos(pos)) {
      if (exact.length) meters = Math.min(...exact);
      else if (g.centre) {
        meters = distanceM(pos.latitude, pos.longitude, g.centre[0], g.centre[1]);
        approx = true;
      }
    }
    return { label: g.label, items: g.items, meters, approx, first: g.first };
  });
  result.sort((a, b) => {
    if (a.meters !== null && b.meters !== null) return a.meters - b.meters || a.label.localeCompare(b.label, "fi");
    if (a.meters !== null) return -1;
    if (b.meters !== null) return 1;
    return a.first - b.first;
  });
  return result.map(({ first, ...g }) => g);
}

/**
 * Etäisyys luettavaksi: "80 m", "1,2 km", "14 km". Pyöristys 10 m:iin alle kilometrillä ja
 * kokonaisiin kilometreihin 10 km:stä alkaen, koska tarkempi luku on silloin väärää tarkkuutta.
 */
export function nearText(meters) {
  if (!Number.isFinite(meters)) return "";
  if (meters < 1000) {
    const rounded = Math.round(meters / 10) * 10;
    return rounded >= 1000 ? "1,0 km" : `${Math.max(10, rounded)} m`;
  }
  const km = meters / 1000;
  if (km >= 10) return `${Math.round(km)} km`;
  return `${km.toLocaleString("fi-FI", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} km`;
}

/** Alueen karkea etäisyys otsikkoon: "noin 14 km" (tarkka sijainti: pelkkä luku). */
export function groupDistanceText(group) {
  if (group.meters === null) return "";
  // Alle 1,5 km:n karkeaa lukua ei näytetä: keskipiste on kilometrin tarkkuudella, joten "noin 300 m" olisi harhaanjohtava.
  // Aluearvio ei ole kohteen etäisyys: se kerrotaan erikseen, ettei "lähellä" näytä todistavan kohteen sijaintia.
  if (group.approx && group.meters < 1500) return "alueella, tarkka sijainti puuttuu";
  return group.approx ? `alue noin ${nearText(group.meters)}, tarkka sijainti puuttuu` : nearText(group.meters);
}

